import {
    useRef,
    useState,
    useEffect,
    useCallback
} from 'react'

import {
    useSearchParams,
    useLoaderData
} from '@remix-run/react'

import { AvailableApps } from '~/config/apps'

import useApp from '~/hooks/useApp'
import useAnalytics, { AnalyticsEvents } from '~/hooks/useAnalytics'

import Header from '~/components/Header'
import HiddenFormInput from '~/components/HiddenFormInput'
import CardsSearchPage from '~/pages/search/CardsSearchPage'

import * as Styled from './style'
import type { SearchRef } from '~/components/Search/types'

const DEFAULT_PAGE = 1
const DEFAULT_HITS_PER_PAGE = 24

const MarketGuideStartPage = () => {
    const mounted = useRef(false)
    const headerRef = useRef<SearchRef>(null)

    const app = useApp()

    const { event: analyticsEvent } = useAnalytics()

    const [searchParams] = useSearchParams()

    const currentQuery = searchParams.get('query') || ''
    const currentPlacements = searchParams.get('placements') || ''
    const currentBranches = searchParams.get('branches') || ''
    const currentCategories = searchParams.get('categories') || ''

    const { payload: searchPayload } = useLoaderData()

    const [page, setPage] = useState(DEFAULT_PAGE)
    const [hitsPerPage] = useState(DEFAULT_HITS_PER_PAGE)

    useEffect(() => {
        if (mounted.current) {
            headerRef.current?.submit()
        }

        mounted.current = true
    }, [page])

    useEffect(() => {
        let itemNameKey = ''

        switch (app.id) {
            case AvailableApps.MarketGuide:
                itemNameKey = 'name'
                break

            case AvailableApps.Jobs:
                itemNameKey = 'title'
                break
        }

        const searchQuery = [
            currentQuery,
            currentPlacements,
            currentBranches,
            currentCategories
        ].filter(i => i).join(',')

        analyticsEvent(AnalyticsEvents.ListView, {
            number_of_results: searchPayload.length,
            search_keywords: searchQuery,
            search_result: Array.isArray(searchPayload) && searchPayload?.map(item => ({
                uuid: item.uuid,
                title: item[itemNameKey]
            }))
        })
    }, [
        analyticsEvent,
        app.id,
        currentQuery,
        currentPlacements,
        currentBranches,
        currentCategories,
        searchPayload
    ])

    const handleOnSearchChange = useCallback(() => {
        setPage(DEFAULT_PAGE)
    }, [])

    const handleOnPaginationChange = useCallback((value: number) => {
        setPage(value)
    }, [])
    
    return (
        <Styled.Form
            method={'get'}
            action={'/'}
        >
            <HiddenFormInput
                name={'page'}
                value={`${page}`}
            />

            <HiddenFormInput
                name={'hitsPerPage'}
                value={`${hitsPerPage}`}
            />

            <Styled.Wrapper>
                <Header
                    ref={headerRef}
                    onSearchChange={handleOnSearchChange}
                />

                <CardsSearchPage
                    page={page}
                    hitsPerPage={hitsPerPage}
                    onPaginationChange={handleOnPaginationChange}
                />
            </Styled.Wrapper>
        </Styled.Form>
    )
}

export default MarketGuideStartPage